import { PsHttpService } from '@patsnap/tahiti-core';
import { resolveNoveltySearchApiPath } from '@/utils/api-path-resolver';

/** 单次请求超时（ms）。须显式传入 post/get：否则 axios 会退回默认 ~62s，导致 /report、/compare 等长任务误报超时 */
const NOVELTY_SEARCH_REQUEST_TIMEOUT_MS = 600_000;

/**
 * Novelty Search 网关：与 telecom 一致按登录态选路径（见 `packages/novelty/vite.config.ts` 代理）
 * - 已登录：`/telecom/novelty_search/{endpoint}`
 * - 游客：`/telecom/novelty_search_guest/{endpoint}`
 */
export abstract class NoveltySearchHttpService extends PsHttpService {
    get options() {
        return {
            baseURL: import.meta.env.DEV ? '' : import.meta.env.VITE_TELECOM_API,
            timeout: NOVELTY_SEARCH_REQUEST_TIMEOUT_MS,
            headers: {
                'Content-Type': 'application/json',
            },
        };
    }

    protected getDefaultHeaders(): Record<string, string> {
        return {
            'X-PatSnap-From': 's-analytics-telecom',
        };
    }

    /**
     * POST 到 Novelty Search 网关，path 为接口名，如 task/init、extract_inventions
     */
    protected async postNoveltyApi<T>(
        path: string,
        data?: unknown,
        config?: { timeout?: number; signal?: AbortSignal }
    ): Promise<T> {
        const cleanPath = path.replace(/^\/+/, '');
        const url = await resolveNoveltySearchApiPath(cleanPath);
        const res = await this.post({
            url,
            data,
            timeout: config?.timeout ?? NOVELTY_SEARCH_REQUEST_TIMEOUT_MS,
            signal: config?.signal,
            headers: {
                ...this.getDefaultHeaders(),
                'Content-Type': 'application/json',
            },
        });
        return res as T;
    }

    /**
     * GET 到 Novelty Search 网关，path 如 task/history；query 拼在 URL 上
     */
    protected async getNoveltyApi<T>(
        path: string,
        query: Record<string, string>,
        config?: { timeout?: number; signal?: AbortSignal }
    ): Promise<T> {
        const cleanPath = path.replace(/^\/+/, '');
        const baseUrl = await resolveNoveltySearchApiPath(cleanPath);
        const qs = new URLSearchParams(query).toString();
        const url = qs ? `${baseUrl}?${qs}` : baseUrl;
        const res = await this.get({
            url,
            method: 'GET',
            timeout: config?.timeout ?? NOVELTY_SEARCH_REQUEST_TIMEOUT_MS,
            signal: config?.signal,
            headers: {
                ...this.getDefaultHeaders(),
            },
        });
        return res as T;
    }

    /**
     * POST 到 Novelty Search 网关并接收二进制（如导出文件）
     */
    protected async postNoveltyApiBlob(
        path: string,
        data?: unknown,
        config?: { timeout?: number; signal?: AbortSignal }
    ): Promise<Blob> {
        const cleanPath = path.replace(/^\/+/, '');
        const url = await resolveNoveltySearchApiPath(cleanPath);
        // Blob 响应非 PatSnapCommonResponseData 信封，与 cc_compare/export 等导出接口一致
        const res = await (this as { post: (opts: Record<string, unknown>) => Promise<Blob> }).post({
            url,
            data,
            timeout: config?.timeout ?? NOVELTY_SEARCH_REQUEST_TIMEOUT_MS,
            signal: config?.signal,
            headers: {
                ...this.getDefaultHeaders(),
                'Content-Type': 'application/json',
            },
            responseType: 'blob',
        });
        return res;
    }
}
